
import type { FloaterStyle } from "../client/combat";

export const ACTOR_SHAKE_DURATION_MS = 100;
/** Ten legacy units, converted using this client's 10 mm legacy-unit scale. */
export const ACTOR_SHAKE_AMPLITUDE_MM = 100;

export interface ShakeEvent {
  readonly entityId: number;
  readonly bornMs: number;
  readonly style: FloaterStyle;
}

export interface ActorShakeOffset {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly active: boolean;
}

function pseudoRandomSign(seed: number): number {
  // A tiny integer hash replaces native rand() without making a replay depend
  // on unrelated particle or UI random calls.
  const value = Math.imul(seed ^ (seed >>> 16), 0x45d9f3b);
  return (value & 1) === 0 ? 1 : -1;
}

/** Return the current visual offset for the most recent landed hit. */
export function actorShakeOffset(
  entityId: number,
  events: readonly ShakeEvent[],
  nowMs: number,
): ActorShakeOffset {
  let latest: ShakeEvent | undefined;
  for (const event of events) {
    if (event.entityId !== entityId || event.style === "miss") continue;
    if (nowMs - event.bornMs < 0 || nowMs - event.bornMs >= ACTOR_SHAKE_DURATION_MS) continue;
    if (latest === undefined || event.bornMs > latest.bornMs) latest = event;
  }
  if (latest === undefined) return { xMm: 0, yMm: 0, zMm: 0, active: false };

  const age = nowMs - latest.bornMs;
  const strength = 1 - age / ACTOR_SHAKE_DURATION_MS;
  const sample = Math.floor(age / 16);
  const seed = Math.imul(entityId | 0, 0x1f123bb5) ^ Math.imul(latest.bornMs | 0, 0x9e3779b9) ^ sample;
  return {
    xMm: pseudoRandomSign(seed) * ACTOR_SHAKE_AMPLITUDE_MM * strength,
    yMm: pseudoRandomSign(seed ^ 0x6d2b79f5) * ACTOR_SHAKE_AMPLITUDE_MM * strength,
    zMm: pseudoRandomSign(seed ^ 0x27d4eb2d) * ACTOR_SHAKE_AMPLITUDE_MM * strength,
    active: true,
  };
}
